import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { BeginHereRail } from '@/components/BeginHere'
import { SignalsAPI, type Signal } from '@/api/signals'
import { SuggestionsAPI, type Suggestion } from '@/api/suggestions'
import { MissionsAPI } from '@/api/missions'
import { useMissions, type Mission } from '@/store/missions'
import { useMissionSelection } from '@/store/missionSelection'
import { useUI } from '@/store/ui'

// The operator's one queue: everything across the app that is waiting on YOUR judgment,
// ranked, each item deep-linking to its judgment surface — except suggestions, which are
// judged right here (accept applies the write, reject closes it). Gates outrank suggestions
// outrank signals: blocked work, then proposed facts, then unread news.

export interface NeedsYouItem {
  key: string
  kind: 'gate' | 'suggestion' | 'signal' | 'proposal'
  glyph: string
  title: string
  detail: string
  go?: () => void
  suggestion?: Suggestion
}

export function useNeedsYou(): { items: NeedsYouItem[]; count: number } {
  const { diwaUrl } = useUI()
  const signalsApi = useMemo(() => new SignalsAPI(diwaUrl), [diwaUrl])
  const suggestionsApi = useMemo(() => new SuggestionsAPI(diwaUrl), [diwaUrl])
  const missionsApi = useMemo(() => new MissionsAPI(diwaUrl), [diwaUrl])
  const navigate = useNavigate()
  const selectMission = useMissionSelection((s) => s.select)

  const { missions } = useMissions()
  const { data: signalsData } = useQuery({
    queryKey: ['signals'],
    queryFn: () => signalsApi.list(),
  })
  const { data: suggestionsData } = useQuery({
    queryKey: ['suggestions', 'open'],
    queryFn: () => suggestionsApi.list('open'),
  })
  const { data: proposalsData } = useQuery({
    queryKey: ['mission-proposals'],
    queryFn: () => missionsApi.listProposals(),
  })
  const signals = signalsData ?? []
  const suggestions = suggestionsData ?? []
  const proposals = proposalsData ?? []

  const items = useMemo<NeedsYouItem[]>(() => {
    const gates = missions
      .filter((m: Mission) => m.steps.some((s) => s.status === 'awaiting_gate'))
      .map((m: Mission) => {
        const step = m.steps.find((s) => s.status === 'awaiting_gate')!
        return {
          key: `gate:${m.id}`,
          kind: 'gate' as const,
          glyph: '◇',
          title: m.title,
          detail: `review gate · ${step.id}`,
          go: () => {
            selectMission(m.id)
            navigate(`/missions/${m.id}`)
          },
        }
      })

    const asks = suggestions.map((s) => ({
      key: `suggestion:${s.id}`,
      kind: 'suggestion' as const,
      glyph: '?',
      title: s.title,
      detail: `${s.kind.replace('_', ' ')} · ${s.payload.source ?? 'evidence'}`,
      suggestion: s,
    }))

    const rank: Record<string, number> = { High: 0, Medium: 1, Low: 2 }
    const fresh = signals
      .filter((s: Signal) => s.status === 'fresh')
      .sort(
        (a, b) =>
          (rank[a.importance] ?? 3) - (rank[b.importance] ?? 3) ||
          b.created_at.localeCompare(a.created_at),
      )
      .map((s: Signal) => ({
        key: `signal:${s.id}`,
        kind: 'signal' as const,
        glyph: '●',
        title: s.title,
        detail: `${s.importance.toLowerCase()} signal · ${s.type.toLowerCase()}`,
        go: () => navigate('/signals'),
      }))

    // Tala's live suggestions — lightest-weight items in the queue: a row per proposal that
    // deep-links to the /missions PROPOSED band (where Promote/Edit/Dismiss live). We don't
    // rebuild those actions here — the mirror only surfaces the count and the link.
    const suggested = proposals.map((p) => ({
      key: `proposal:${p.id}`,
      kind: 'proposal' as const,
      glyph: '✦',
      title: `${p.recipe_name}: ${p.target}`,
      detail: `suggested · ${p.domain}`,
      go: () => navigate('/missions'),
    }))

    return [...gates, ...asks, ...fresh, ...suggested]
  }, [missions, signals, suggestions, proposals, navigate, selectMission])

  return { items, count: items.length }
}

function SuggestionRow({ item }: { item: NeedsYouItem }) {
  const { diwaUrl } = useUI()
  const api = useMemo(() => new SuggestionsAPI(diwaUrl), [diwaUrl])
  const qc = useQueryClient()
  const s = item.suggestion!

  async function resolve(decision: 'accept' | 'reject') {
    try {
      await api.resolve(s.id, decision)
    } finally {
      await qc.invalidateQueries({ queryKey: ['suggestions', 'open'] })
      await qc.invalidateQueries({ queryKey: ['people'] })
    }
  }

  const facts = Object.entries(s.payload.normalized ?? {})
    .filter(([k]) => !['first_name', 'last_name'].includes(k))
    .slice(0, 3)

  return (
    <div className="dw-queue-row dw-queue-row--suggestion">
      <span className="dw-queue-glyph dw-queue-glyph--gate">{item.glyph}</span>
      <span className="dw-queue-title">
        {item.title}
        <span className="dw-inbox-detail">{item.detail}</span>
        {facts.length > 0 && (
          <span className="dw-inbox-facts">
            {facts.map(([k, v]) => `${k.replace('_', ' ')}: ${v}`).join(' · ')}
          </span>
        )}
        <span className="dw-inbox-actions">
          <button type="button" className="dw-inbox-btn dw-inbox-btn--yes" onClick={() => resolve('accept')}>
            Accept
          </button>
          <button type="button" className="dw-inbox-btn" onClick={() => resolve('reject')}>
            Reject
          </button>
        </span>
      </span>
    </div>
  )
}

export function NeedsYou() {
  const { items } = useNeedsYou()

  return (
    <div>
      {/* The guided first steps ride every page at the top of ◈ — minimizable, dismissible. */}
      <BeginHereRail />
      <div className="dw-rail-section-title">Needs you</div>
      {items.length === 0 ? (
        <div className="dw-rail-empty">Nothing needs you — the machine is working.</div>
      ) : (
        <div className="dw-mission-queue">
          {items.map((item) =>
            item.kind === 'suggestion' ? (
              <SuggestionRow key={item.key} item={item} />
            ) : (
              <button key={item.key} type="button" className="dw-queue-row" onClick={item.go}>
                <span className={`dw-queue-glyph dw-queue-glyph--${item.kind === 'gate' ? 'gate' : 'running'}`}>
                  {item.glyph}
                </span>
                <span className="dw-queue-title">
                  {item.title}
                  <span className="dw-inbox-detail">{item.detail}</span>
                </span>
              </button>
            ),
          )}
        </div>
      )}
    </div>
  )
}
